import { useRef, useState } from "react";
import { Upload, Loader2, X, FileText, Film } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatErr } from "../../lib/api";

export const uploadFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/media", fd);
  return data;
};

export const PageTitle = ({ title, desc, children }) => (
  <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
    <div>
      <h1 className="font-display text-4xl font-extrabold uppercase text-slate-950">{title}</h1>
      {desc && <p className="mt-1 text-sm text-slate-600">{desc}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

const Preview = ({ url }) => {
  if (!url) return null;
  const u = url.toLowerCase();
  if (u.endsWith(".pdf")) return <span className="flex h-16 w-16 items-center justify-center bg-slate-100"><FileText className="h-6 w-6 text-slate-500" /></span>;
  if (u.endsWith(".mp4") || u.endsWith(".webm")) return <span className="flex h-16 w-16 items-center justify-center bg-slate-100"><Film className="h-6 w-6 text-slate-500" /></span>;
  return <img src={fileUrl(url)} alt="" className="h-16 w-16 object-cover" />;
};

export const MediaField = ({ label, value, onChange, accept = "image/*", testId, hint }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const m = await uploadFile(file);
      onChange(m.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div data-testid={testId}>
      <p className="mb-1.5 text-sm font-medium text-slate-800">{label}</p>
      <div className="flex items-center gap-3">
        <Preview url={value} />
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Upload a file or paste a link" className="min-w-0 flex-1 border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#0a6cff]" />
        <input ref={ref} type="file" accept={accept} className="hidden" onChange={pick} data-testid={`${testId}-file`} />
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="inline-flex items-center gap-1.5 bg-slate-900 px-3 py-2 text-xs font-semibold text-white disabled:opacity-60" data-testid={`${testId}-upload`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
        </button>
        {value && <button type="button" onClick={() => onChange("")} className="p-1 text-slate-400 hover:text-red-500" aria-label="Clear"><X className="h-4 w-4" /></button>}
      </div>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
};

export const TextField = ({ label, value, onChange, testId, textarea, hint, ...rest }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium text-slate-800">{label}</span>
    {textarea ? (
      <textarea data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={4} className="w-full border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#0a6cff]" {...rest} />
    ) : (
      <input data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#0a6cff]" {...rest} />
    )}
    {hint && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
  </label>
);
